import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';
export interface Money { value: string; digits: number; currency?: string }

export const PERIODS = [
  ['TODAY', '今日'], ['YESTERDAY', '昨日'], ['WTD', '本周'], ['LAST_WEEK', '上周'],
  ['MTD', '本月'], ['LAST_MONTH', '上月'], ['QTD', '本季度'], ['YTD', '本年'],
  ['D7', '近7天'], ['D30', '近30天'], ['D90', '近90天'], ['Y1', '近一年'], ['ALL', '累计'], ['CUSTOM', '自定义日期'],
] as const;
export type Period = typeof PERIODS[number][0];
export type Granularity = 'day' | 'week' | 'month';
export interface PeriodPerformance {
  period: string;
  requestedStart: string;
  requestedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  netProfit: Money | null;
  realizedNet: Money | null;
  floatingChange: Money | null;
  deposits: Money | null;
  withdrawals: Money | null;
  otherAdjustments: Money | null;
  startEquity: Money | null;
  endEquity: Money | null;
  returnPct: number | null;
  factor: number | null;
  method: 'TWR' | 'MODIFIED_DIETZ' | 'UNAVAILABLE';
  status: 'OK' | 'ESTIMATED' | 'UNAVAILABLE';
  reasons: string[];
}
export interface AccountPerformance {
  asOf: string | null;
  generatedAt: string;
  coverageStart: string | null;
  stale: boolean;
  boundaryMinutes: number;
  algorithmVersion: string;
  selected: PeriodPerformance;
  comparison: { previous: PeriodPerformance; profitChange: Money | null; returnDifferencePp: number | null } | null;
  summary: PeriodPerformance[];
  curve: PeriodPerformance[];
  rows: PeriodPerformance[];
  months: PeriodPerformance[];
}
export interface PerformanceParams { period?: Period; from?: string; to?: string; granularity?: Granularity }
export const getPerformance = async (params: PerformanceParams) =>
  (await http.get<AccountPerformance>('/admin/api/v1/real-account/performance', { params })).data;
export function usePerformance(params: PerformanceParams = { period: 'MTD', granularity: 'day' }) {
  return useQuery({
    queryKey: ['admin', 'performance', params], queryFn: () => getPerformance(params),
    staleTime: 30_000, refetchInterval: 60_000,
  });
}
export const periodLabel = (value: string) => PERIODS.find(([p]) => p === value)?.[1] ?? value;

export const REASONS: Record<string, string> = {
  EMPTY_PERIOD: '该周期尚无可统计数据', NO_HISTORY: '暂无账户历史', BEFORE_COVERAGE: '期初早于可验证历史',
  START_VALUATION_MISSING: '缺少期初净值', END_VALUATION_MISSING: '缺少期末净值', INSUFFICIENT_VALUATIONS: '净值记录不足',
  UNCLASSIFIED_ADJUSTMENT: '存在待确认的资金调整', CASH_AMOUNT_MISSING: '资金流水金额缺失',
  FLOW_VALUATION_MISSING: '缺少出入金时点净值', FLOW_VALUATION_ESTIMATED: '资金流时点净值不足，按资金停留时间估算',
  NON_POSITIVE_EQUITY: '净值为零或负数，收益率不可计算', ZERO_BASE_UNEXPLAINED_CHANGE: '零资金期间出现未核实的净值变化',
  CAPITAL_RESTART_AFTER_LOSS: '资金亏损归零后重新入金，不能连接收益率', NO_INVESTED_CAPITAL: '该区间没有投入资金',
  INVALID_CASH_FLOW: '资金流水不一致', NON_FINITE_RETURN: '收益率超出可计算范围', RETURN_NOT_COMPUTABLE: '收益率暂不可计算',
};
export const reasonText = (p: PeriodPerformance) => p.reasons.map((r) => REASONS[r] ?? r).join('；');
